import { useEffect, useState } from "react"
import { CurrencyConvertion, PriceCheck, QatarConvertion, FormatValueDecimal } from "../../utils/Validate";
import AmountModeField from "../../components/common/AmountModeField";
import FetchData from "../../providers/dataProvider";
import SpecialColorDetailsModal from "../../components/common/SpecialColorDetailsModal";

const OrderProductSummary = ({ values: orderValues, setValues, currencyDetail: orderCurrency, setQuotUserEntry, quotUserEntry }) => {

    // Company (distributor) default currency = same currency as Quotation summary
    const { data: allCurrencies } = FetchData("currency");
    const { data: allDistributors } = FetchData("distributor");
    const orderDistributor = allDistributors?.find((item) => item?.id === orderValues?.distributorId);
    const baseCurrency = allCurrencies?.find((item) => item?.id === orderDistributor?.currencyId);

    const currencyDetail = baseCurrency?.shortCode ? baseCurrency : orderCurrency;
    const values = baseCurrency?.shortCode ? { ...orderValues, conversionRate: 1 } : orderValues;

    const [quotationSummary, setQuotationSummary] = useState({});
    const [taxMode, setTaxMode] = useState('$');
    const [customDutyMode, setCustomDutyMode] = useState('$');
    const [clearnceMode, setClearnceMode] = useState('$');
    const [specialColorModalShow, setSpecialColorModalShow] = useState(false);
    const [hasUserEdited, setHasUserEdited] = useState(
        () => (values?.grandTotal > 0 || values?.netPrice > 0)
    );

    const HandleCustomDutyModeChange = () => {
        setCustomDutyMode((prevMode) => (prevMode === '$' ? '%' : '$'));
        setQuotUserEntry({ ...quotUserEntry, customDutyPercentage: 0, customDuty: 0 })
    };

    const HandleClearnceModeChange = () => {
        setClearnceMode((prevMode) => (prevMode === '$' ? '%' : '$'));
        setQuotUserEntry({ ...quotUserEntry, clearanceChargesPercentage: 0, clearanceCharges: 0 })
    };

    const HandleTaxModeChange = () => {
        setTaxMode((prevMode) => (prevMode === '$' ? '%' : '$'));
        setQuotUserEntry({ ...quotUserEntry, tax: 0, taxPercentage: 0 })
    };

    useEffect(() => {
        if (!values?.products || values.products.length === 0) return;

        let totListPrice = 0;
        let totNetPrice = 0;

        values.products.forEach((item) => {
            totListPrice += (item?.listPrice * 1) || 0;
            totNetPrice += (item?.sellingPrice * 1) || 0;
        });

        setQuotationSummary({
            totalListPrice: totListPrice,
            netPrice: totNetPrice,
        });
    }, [values?.products]);

    useEffect(() => {
        setValues((prev) => ({
            ...prev,
            totalListPrice: quotationSummary?.totalListPrice,
            netPrice: quotationSummary?.netPrice,
        }));
    }, [quotationSummary]);

    useEffect(() => {
        if ((quotationSummary?.netPrice > 0 || values?.netPrice > 0) && !hasUserEdited) {
            setHasUserEdited(true);
        }
    }, [quotationSummary?.netPrice, values?.netPrice]);

    const calculateTotalDdp = (item, freightCustoms) => {
        const sellingPrice = (item?.sellingPrice * 1) || 0;

        if (freightCustoms >= 1) return 0;

        const ddpMultiplier = 1 - freightCustoms;
        const finalDdp = sellingPrice / (ddpMultiplier || 1);

        if (!Number.isFinite(finalDdp)) return 0;

        return parseFloat(finalDdp.toFixed(2));
    };

    useEffect(() => {
        const totalExworksPrice = (values?.netPrice * 1) + (values.specialCharges * 1)
        const customDutyValue = customDutyMode === '$' ? (values?.customDuty * 1) : (((totalExworksPrice * 1) + (values?.freightCharges * 1)) * ((values?.customDutyPercentage * 1) / 100))
        const clearnceValue = clearnceMode === '$' ? (values?.clearanceCharges * 1) : (((totalExworksPrice * 1) + (values?.freightCharges * 1) + (customDutyValue * 1)) * ((values?.clearanceChargesPercentage * 1) / 100))
        const totalLandedCost = ((totalExworksPrice * 1) + (values?.freightCharges * 1) + (customDutyValue * 1) + (clearnceValue * 1))
        const marginFraction = Math.min((values?.salesMarginPercentage * 1 || 0) / 100, 0.999999)
        const salesMarginValue = (totalLandedCost * 1) / (1 - marginFraction)
        const taxValue = taxMode === '$' ? (values?.tax * 1) : ((salesMarginValue * 1) * ((values?.taxPercentage * 1) / 100))
        const grandTotal = salesMarginValue + taxValue
        const frieghtcustoms = salesMarginValue > 0
            ? (salesMarginValue - (values?.netPrice * 1)) / salesMarginValue
            : 0
        const productsArr = values?.products?.map((item) => {
            const totalDdp = calculateTotalDdp(item, frieghtcustoms);
            return { ...item, totalDdp };
        });

        setValues((prev) => ({
            ...prev,
            products: productsArr,
            totalSellingPrice: hasUserEdited ? parseFloat(salesMarginValue) : 0,
            totalExworksPrice: hasUserEdited ? parseFloat(totalExworksPrice) : 0,
            grandTotal: hasUserEdited ? parseFloat(grandTotal) : 0,
            totalLandedCost: hasUserEdited ? parseFloat(totalLandedCost) : 0,
        }));
    }, [
        values?.specialCharges,
        values?.netPrice,
        values?.customDuty,
        values?.customDutyPercentage,
        values?.freightCharges,
        values?.clearanceCharges,
        values?.clearanceChargesPercentage,
        values?.salesMarginPercentage,
        values?.tax,
        values?.taxPercentage,
        values?.products?.length,
        clearnceMode, customDutyMode, taxMode, hasUserEdited
    ]);

    useEffect(() => {
        if (values?.customDuty > 0) setCustomDutyMode('$')
        else setCustomDutyMode('%')
        if (values?.tax > 0) setTaxMode('$')
        else setTaxMode('%')
        if (values?.clearanceCharges > 0) setClearnceMode('$')
        else setClearnceMode('%')

        if (values?.grandTotal > 0) {
            setHasUserEdited(true);
        }

        setQuotUserEntry({
            // Special-color charges are already stored in their final amount.
            // Do not currency-convert this field before saving the order.
            specialCharges: values?.specialCharges || 0,
            freightCharges: FormatValueDecimal(CurrencyConvertion(values?.freightCharges, values?.conversionRate, currencyDetail) * 1) || 0,
            customDuty: FormatValueDecimal(CurrencyConvertion(values?.customDuty, values?.conversionRate, currencyDetail) * 1) || 0,
            customDutyPercentage: values?.customDutyPercentage || 0,
            clearanceCharges: FormatValueDecimal(CurrencyConvertion(values?.clearanceCharges, values?.conversionRate, currencyDetail) * 1) || 0,
            clearanceChargesPercentage: values?.clearanceChargesPercentage || 0,
            salesMarginPercentage: values?.salesMarginPercentage || 0,
            tax: FormatValueDecimal(CurrencyConvertion(values?.tax, values?.conversionRate, currencyDetail) * 1) || 0,
            taxPercentage: values?.taxPercentage || 0,
        });
    }, [values.id, values?.quotationId, currencyDetail?.shortCode, values?.conversionRate, values?.grandTotal])

    const HandleInputChange = (e) => {
        const { name, value } = e.target;
        setQuotUserEntry({ ...quotUserEntry, [name]: value || 0 })
        if (!hasUserEdited) setHasUserEdited(true);
    }

    useEffect(() => {
        // The summary first renders with an empty quotUserEntry object. Do not
        // let that initial render replace the quotation's special charge with
        // zero before the display values have been hydrated.
        if (!Object.prototype.hasOwnProperty.call(quotUserEntry, "specialCharges")) {
            return;
        }

        setValues((prev) => ({
            ...prev,
            specialCharges: Number(quotUserEntry?.specialCharges) || 0,
            freightCharges: QatarConvertion(quotUserEntry?.freightCharges, values?.conversionRate, currencyDetail) * 1 || 0,
            customDuty: QatarConvertion(quotUserEntry?.customDuty, values?.conversionRate, currencyDetail) * 1 || 0,
            customDutyPercentage: (quotUserEntry?.customDutyPercentage) * 1 || 0,
            clearanceCharges: QatarConvertion(quotUserEntry?.clearanceCharges, values?.conversionRate, currencyDetail) * 1 || 0,
            clearanceChargesPercentage: (quotUserEntry?.clearanceChargesPercentage) * 1 || 0,
            salesMarginPercentage: (quotUserEntry?.salesMarginPercentage) * 1 || 0,
            tax: QatarConvertion(quotUserEntry?.tax, values?.conversionRate, currencyDetail) * 1 || 0,
            taxPercentage: (quotUserEntry?.taxPercentage) * 1 || 0
        }))
    }, [quotUserEntry])

    return (
        <div className="col-xxl-2 col-xl-2 col-lg-3 col-md-12 col-sm-12 col-xs-12 right-side-card order-summary-side-card">
            <div className="card order-commercial-summary">
                <div className="card-header">
                    <h5>{currencyDetail?.shortCode}: {FormatValueDecimal(CurrencyConvertion(values?.grandTotal, values?.conversionRate, currencyDetail))}</h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                            <div className="accordion" id="orderSummaryAccordion">
                                <div className="row">
                                    <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                                        <div className="mb-2">
                                            <label>Net price</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="netPrice"
                                                placeholder="Enter"
                                                value={FormatValueDecimal(CurrencyConvertion(values?.netPrice, values?.conversionRate, currencyDetail))}
                                                disabled
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>

                                        <div className="mb-2 ">
                                            <div className="d-flex align-items-center">
                                                <label className="mr-1">Special color</label>
                                                <button
                                                    type="button"
                                                    className="btn p-0 mb-1"
                                                    aria-label="Open special color details"
                                                    onClick={() => setSpecialColorModalShow(true)}
                                                >
                                                    <i className="fas fa-angle-right"></i>
                                                </button>
                                            </div>
                                            <input
                                                autoComplete="off"
                                                type="number"
                                                name="specialCharges"
                                                step=".01"
                                                min="0"
                                                className="form-control"
                                                placeholder="Enter"
                                                onChange={HandleInputChange}
                                                value={quotUserEntry.specialCharges > 0 ? quotUserEntry.specialCharges : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                style={{ textAlign: "right" }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Total Ex-Works price</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="totalExworksPrice"
                                                placeholder="Enter"
                                                value={FormatValueDecimal(CurrencyConvertion(values?.totalExworksPrice, values?.conversionRate, currencyDetail))}
                                                disabled
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>

                                        <div className="mb-2">
                                            <label>Frieght & delivery charges</label>
                                            <input
                                                autoComplete="off"
                                                type="number"
                                                name="freightCharges"
                                                step=".01"
                                                min="0"
                                                className="form-control"
                                                placeholder="Enter"
                                                onChange={HandleInputChange}
                                                value={quotUserEntry?.freightCharges > 0 ? quotUserEntry?.freightCharges : ""}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                style={{ textAlign: "right" }}
                                                disabled
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Custom Duty</label>
                                            <AmountModeField
                                                name={customDutyMode === '$' ? 'customDuty' : 'customDutyPercentage'}
                                                step=".01"
                                                min="0"
                                                onChange={HandleInputChange}
                                                onModeToggle={HandleCustomDutyModeChange}
                                                mode={customDutyMode}
                                                value={customDutyMode === '$'
                                                    ? (quotUserEntry?.customDuty > 0 ? quotUserEntry?.customDuty : "")
                                                    : (values?.customDutyPercentage > 0 ? values?.customDutyPercentage : "")}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                                disabled
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label>Clearance charges</label>
                                            <AmountModeField
                                                name={clearnceMode === '$' ? 'clearanceCharges' : 'clearanceChargesPercentage'}
                                                step=".01"
                                                min="0"
                                                onChange={HandleInputChange}
                                                onModeToggle={HandleClearnceModeChange}
                                                mode={clearnceMode}
                                                value={clearnceMode === '$'
                                                    ? (quotUserEntry?.clearanceCharges > 0 ? quotUserEntry?.clearanceCharges : "")
                                                    : (values?.clearanceChargesPercentage > 0 ? values?.clearanceChargesPercentage : "")}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                                disabled
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Total Landed Cost</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder="Enter"
                                                name="totalLandedCost"
                                                disabled
                                                value={values?.totalLandedCost > 0 ? FormatValueDecimal(CurrencyConvertion(values?.totalLandedCost, values?.conversionRate, currencyDetail)) : "0.00"}
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>
                                        <div className="mb-2">
                                            <label>Total Selling Price</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="totalSellingPrice"
                                                placeholder="Enter"
                                                value={FormatValueDecimal(CurrencyConvertion(values?.totalSellingPrice, values?.conversionRate, currencyDetail))}
                                                disabled
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>
                                        <div className="mb-2">
                                            <label>Tax</label>
                                            <AmountModeField
                                                name={taxMode === '$' ? 'tax' : 'taxPercentage'}
                                                step=".01"
                                                min="0"
                                                onChange={HandleInputChange}
                                                onModeToggle={HandleTaxModeChange}
                                                mode={taxMode}
                                                value={taxMode === '$'
                                                    ? (quotUserEntry?.tax > 0 ? quotUserEntry?.tax : "")
                                                    : (values?.taxPercentage > 0 ? values?.taxPercentage : "")}
                                                onKeyPress={(e) => { return PriceCheck(e.target.value) }}
                                                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                                                disabled
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label>Grand Total</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder="Enter"
                                                name="grandTotal"
                                                disabled
                                                value={values?.grandTotal > 0 ? FormatValueDecimal(CurrencyConvertion(values?.grandTotal, values?.conversionRate, currencyDetail)) : "0.00"}
                                                style={{ textAlign: "right" }}
                                            ></input>
                                        </div>
                                    </div>
                                </div>
                                {/* </div> */}
                                {/* </div> */}
                                {/* </div>
                                </div> */}
                                <div className="card d-none">
                                    <div className="card-header" id="headingTwo">
                                        <h2 className="mb-0">
                                            <button
                                                className="btn btn-link btn-block text-left"
                                                type="button"
                                                data-toggle="collapse"
                                                data-target="#collapseOne"
                                                aria-expanded="true"
                                                aria-controls="collapseOne"
                                            >
                                                Account Summary
                                            </button>
                                        </h2>
                                    </div>

                                    <div
                                        id="collapseOne"
                                        className="collapse show"
                                        aria-labelledby="headingOne"
                                        data-parent="#orderSummaryAccordion"
                                    >
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-12 mb-2">
                                                    <label>Credit Limit</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Outstanding</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Overdue</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Pending Orders</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Pending Delivery</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                                <div className="col-md-12 mb-2">
                                                    <label>Available Room</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder=""
                                                    ></input>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SpecialColorDetailsModal
                show={specialColorModalShow}
                onHide={() => setSpecialColorModalShow(false)}
                values={values}
                currencyDetail={currencyDetail}
                specialCharge={
                    quotUserEntry?.specialCharges ||
                    values?.specialCharges ||
                    values?.specialColorCharges ||
                    0
                }
            />
        </div>
    );
};

export default OrderProductSummary;